import type React from 'react';

import type { Behandlingsgruppe } from '../types/Helsenorge.VelgBehandlingssted.Libraries.EntitiesEntities';
import type { HNVelgBehandlingsstedFrontend } from '../types/Resources';

import ExpanderList from '@helsenorge/designsystem-react/components/ExpanderList';
import LazyIcon from '@helsenorge/designsystem-react/components/LazyIcon';
import Loader from '@helsenorge/designsystem-react/components/Loader';
import NotificationPanel from '@helsenorge/designsystem-react/components/NotificationPanel';
import Title from '@helsenorge/designsystem-react/components/Title';

import BehandlingerTable from './behandlinger-table';
import { type TabellData, getTotalVentetidForBehandlingsgruppe } from './table-helper';
import { useResources } from '../resources';
import AdresseComponent from '../shared/adresse';
import TelefonComponent from '../shared/telefon';
import { useGetVentetiderForBehandlingsstedQuery } from '../store/api/velgBehandlingsstedApi';
import { behandlingSvgIkonMapper } from '../utils/behandling-ikon-mapper';
import { HN_VELG_BEHANDLINGSSTED_FRONTEND } from '../utils/resources-project';

interface Props {
  rowData?: TabellData;
  expanded?: boolean;
}

const BehandlingsstedExpanded: React.FunctionComponent<Props> = ({ rowData, expanded }: Props) => {
  const resources = useResources<HNVelgBehandlingsstedFrontend>(HN_VELG_BEHANDLINGSSTED_FRONTEND);

  const orgnr = rowData?.orgnr ?? '';
  const behandlingsstedData = rowData?.behandlingsstedData;

  const { data, isLoading, isError, isSuccess } = useGetVentetiderForBehandlingsstedQuery(orgnr, {
    skip: !expanded || !orgnr,
  });

  const behandlingsGrupper = data?.ventetiderForBehandlingssted?.behandlingsGrupper;

  if (!orgnr) {
    return null;
  }

  if (isLoading) {
    return (
      <div className="vbs-behandlingssteder__loader-wrapper">
        <Loader size="medium" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="vbs-behandlingssteder__error-wrapper">
        <NotificationPanel variant="error" label={resources?.tekniskFeilVentetiderForBehandlingssted} />
      </div>
    );
  }

  return isSuccess && behandlingsstedData && resources ? (
    <>
      <div className="vbs-main__expanded-inner-content">
        <Title htmlMarkup="h2" appearance={'title2'} margin={1}>
          {behandlingsstedData.navnBedriftsregister}
        </Title>

        <p>{`${resources.behandlingsstederOrgnr} ${behandlingsstedData.orgnr}`}</p>

        {!!behandlingsstedData.besoksAdresse && !!behandlingsstedData.besoksAdresse.gateadresse && (
          <>
            <Title htmlMarkup="h3" appearance={'title4'} margin={0.5}>
              {resources.tabellBesoksadresse}
            </Title>
            <AdresseComponent adresse={behandlingsstedData.besoksAdresse} />
          </>
        )}

        {!!behandlingsstedData.telefon && (
          <>
            <Title htmlMarkup="h3" appearance={'title4'} margin={0.5}>
              {resources.tabellTelefon}
            </Title>
            <TelefonComponent telefon={behandlingsstedData.telefon} />
          </>
        )}

        <br />
      </div>

      {behandlingsGrupper && (
        <ExpanderList color="neutral">
          {behandlingsGrupper.map((behandlingsGruppe: Behandlingsgruppe, index: number) => {
            return (
              <ExpanderList.Expander
                key={behandlingsGruppe.navn}
                icon={<LazyIcon iconName={behandlingSvgIkonMapper(behandlingsGruppe.kode)} />}
                title={
                  <div>
                    <span>{`${getTotalVentetidForBehandlingsgruppe(
                      resources,
                      behandlingsGruppe.ventetidMin,
                      behandlingsGruppe.ventetidMax
                    )} ${behandlingsGruppe.navn} `}</span>
                    <span className="vbs-main__subtitle">{`(${behandlingsGruppe.behandlinger?.length})`}</span>
                  </div>
                }
              >
                <BehandlingerTable
                  behandlingsGruppe={behandlingsGruppe}
                  resources={resources}
                  behandlingssted={behandlingsstedData.navnBedriftsregister ? behandlingsstedData.navnBedriftsregister : ''}
                  index={index}
                />
              </ExpanderList.Expander>
            );
          })}
        </ExpanderList>
      )}
    </>
  ) : null;
};

export default BehandlingsstedExpanded;
